import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Alert,
  Typography,
  Link,
  CircularProgress,
  Box,
  Stack,
  Chip,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { api } from '../api/client.js';
import { safeHttpUrl } from '../lib/safeUrl.js';
import { useT } from '../i18n/useT.js';

/** Providers whose adapter implements createIssue(), with a short glyph for tabs. */
const ISSUE_PROVIDERS: Record<string, { name: string; glyph: string }> = {
  jira: { name: 'Jira', glyph: 'J' },
  freshdesk: { name: 'Freshdesk', glyph: 'F' },
  servicenow: { name: 'ServiceNow', glyph: 'S' },
  'azure-devops': { name: 'Azure DevOps', glyph: 'A' },
  github: { name: 'GitHub', glyph: 'G' },
  gitlab: { name: 'GitLab', glyph: 'L' },
};

const ISSUE_TYPES = ['Bug', 'Task', 'Story', 'Incident'];
const PRIORITIES = ['Highest', 'High', 'Medium', 'Low'];

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string;
  projectId: string;
  sessionId: string;
  defaultTitle: string;
  /** Ticket description / context. */
  description?: string;
  /** Ticket tags → issue labels. */
  tags?: string[];
  /** Suggested priority from the ticket, e.g. 'Highest'. */
  defaultPriority?: string;
  assigneeName?: string;
  /** Session AI summary text, for the "AI Summary" attachment. */
  aiSummary?: string;
  /** JS error count on the session, for the "Error Logs" attachment. */
  jsErrors?: number;
}

interface TrackerProfile {
  id: string;
  provider: string;
  label: string;
  projectName: string;
  target: string;
}

/**
 * Creates a tracker issue from a session in one click, matching the ticket UX:
 * pick a connected tracker, set type/priority/labels, and choose which session
 * artefacts (replay link, AI summary, error logs) to attach. The chosen
 * artefacts are assembled into the issue description before it is filed.
 */
export function CreateIssueDialog({
  open,
  onClose,
  orgId,
  projectId,
  sessionId,
  defaultTitle,
  description,
  tags,
  defaultPriority,
  assigneeName,
  aiSummary,
  jsErrors,
}: Props) {
  const all = useT();
  const c = all.integrations.createIssue;
  const common = all.common;
  const base = `/v1/orgs/${orgId}/projects/${projectId}/integrations`;
  const [trackerId, setTrackerId] = useState('');
  const [title, setTitle] = useState(defaultTitle);
  const [issueType, setIssueType] = useState('Bug');
  const [priority, setPriority] = useState(defaultPriority ?? 'High');
  const [attachReplay, setAttachReplay] = useState(true);
  const [attachSummary, setAttachSummary] = useState(true);
  const [attachErrors, setAttachErrors] = useState(true);

  // Every issue-capable profile in the org — a team's tracker serves sessions
  // from any project, so the picker is not limited to this one's.
  const integrations = useQuery({
    enabled: open && !!orgId && !!projectId,
    queryKey: ['org-trackers', orgId],
    queryFn: async () => (await api.get(`${base}/trackers`)).data.data as TrackerProfile[],
  });

  // The project's configured route (Tickets → Configuration) wins as default.
  const project = useQuery({
    enabled: open && !!orgId && !!projectId,
    queryKey: ['project', projectId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${projectId}`)).data as {
        ticketIntegrationId?: string | null;
      },
  });

  const trackers = integrations.data ?? [];
  const routed = trackers.find((t) => t.id === project.data?.ticketIntegrationId);
  const active = trackers.find((t) => t.id === trackerId) ?? routed ?? trackers[0];
  const activeProvider = active?.provider ?? '';
  const replayUrl = `${window.location.origin}/projects/${projectId}/sessions/${sessionId}`;
  const labels = tags ?? [];

  // Assemble the issue description from the selected attachments.
  const body = useMemo(() => {
    const parts: string[] = [];
    if (description) parts.push(description);
    parts.push(`**Type:** ${issueType}  ·  **Priority:** ${priority}`);
    if (attachReplay) parts.push(`**Session replay:** ${replayUrl}`);
    if (attachSummary && aiSummary) parts.push(`**AI summary**\n${aiSummary}`);
    if (attachErrors) parts.push(`**Error logs:** ${jsErrors ?? 0} JS error(s) captured on the session.`);
    return parts.join('\n\n');
  }, [description, issueType, priority, attachReplay, attachSummary, aiSummary, attachErrors, jsErrors, replayUrl]);

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post(`${base}/${activeProvider}/issues`, {
          sessionId,
          title,
          body,
          labels,
          // The chosen profile, wherever in the org it lives.
          integrationId: active?.id,
        })
      ).data as { externalId: string; url: string },
  });

  const providerName = ISSUE_PROVIDERS[activeProvider]?.name ?? activeProvider ?? 'issue tracker';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
        <BugReportIcon sx={{ color: '#e11d48' }} />
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
          Create Bug from Session Replay
        </Typography>
      </Stack>
      <Divider />

      <DialogContent>
        {integrations.isLoading && <CircularProgress size={20} />}
        {!integrations.isLoading && trackers.length === 0 && (
          <Alert severity="info">
            {c.noTracker}
          </Alert>
        )}

        {trackers.length > 0 && !create.data && (
          <>
            {/* Provider tabs */}
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={active?.id ?? ''}
              onChange={(_, v) => v && setTrackerId(v)}
              sx={{ mb: 2.5, mt: 1 }}
            >
              {trackers.map((t) => (
                <ToggleButton key={t.id} value={t.id} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  <Box
                    component="span"
                    sx={{ mr: 1, px: 0.8, borderRadius: 1, bgcolor: 'action.hover', fontSize: 13 }}
                  >
                    {ISSUE_PROVIDERS[t.provider]?.glyph ?? t.provider[0]?.toUpperCase()}
                  </Box>
                  {ISSUE_PROVIDERS[t.provider]?.name ?? t.provider}
                  {t.label !== 'default' ? ` (${t.label})` : ''}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Typography variant="caption" color="text.secondary">{c.summary}</Typography>
            <TextField fullWidth size="small" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mb: 2, mt: 0.5 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField select size="small" label={c.issueType} value={issueType} onChange={(e) => setIssueType(e.target.value)}>
                {ISSUE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField select size="small" label={c.priority} value={priority} onChange={(e) => setPriority(e.target.value)}>
                {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Box>

            {assigneeName && (
              <>
                <Typography variant="caption" color="text.secondary">{c.assignee}</Typography>
                <TextField fullWidth size="small" value={assigneeName} disabled sx={{ mb: 2, mt: 0.5 }} />
              </>
            )}

            {labels.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">{c.labels}</Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  {labels.map((l) => <Chip key={l} size="small" label={l} />)}
                </Stack>
              </Box>
            )}

            {/* Auto-attach from session */}
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{c.autoAttach}</Typography>
              <AttachRow
                label={c.replayUrl}
                hint={c.replayHint}
                checked={attachReplay}
                onChange={setAttachReplay}
              />
              <AttachRow
                label={c.aiSummary}
                hint={aiSummary ? c.aiSummaryHint : c.aiSummaryNone}
                checked={attachSummary && !!aiSummary}
                disabled={!aiSummary}
                onChange={setAttachSummary}
              />
              <AttachRow
                label={c.errorLogs}
                hint={c.errorLogsHint(jsErrors ?? 0)}
                checked={attachErrors}
                onChange={setAttachErrors}
              />
            </Box>

            {create.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {(create.error as { response?: { data?: { error?: { message?: string } } } })
                  ?.response?.data?.error?.message ??
                  c.failed}
              </Alert>
            )}
          </>
        )}

        {create.data && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Issue created:{' '}
            <Link href={safeHttpUrl(create.data.url)} target="_blank" rel="noreferrer">
              {create.data.externalId}
            </Link>
          </Alert>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {create.data ? c.filed : c.creates(providerName)}
        </Typography>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>{create.data ? common.close : common.cancel}</Button>
          {trackers.length > 0 && !create.data && (
            <Button
              variant="contained"
              startIcon={<BugReportIcon />}
              disabled={create.isPending || !title.trim() || !activeProvider}
              onClick={() => create.mutate()}
            >
              {create.isPending ? c.creating : c.create}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function AttachRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.75 }}>
      <Box>
        <Typography variant="body2" fontWeight={600}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
      </Box>
      <Switch checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </Stack>
  );
}
